import { useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { useCart } from '../auth/hooks/useCart'
import { useMe } from '../auth/hooks/useMe'
import {
  branchPriorityKey,
  buildBranchPriorityStorageKey,
  tickBranchPriority,
  untickBranchPriority,
} from './useBranchPriority'
import {
  checkPostcode,
  describeFailedPostcodeCheck,
} from '../services/postcodes'
import {
  parseEngineerSession,
  readEngineerSessionRaw,
  writeEngineerSession,
} from '../services/engineerSession'
import {
  buildRecentLocationsKey,
  rememberRecentLocation,
} from '../services/recentLocations'
import type { EngineerSessionAtBranch } from '../services/engineerSession'
import type {
  PostcodeCoordinates,
  PostcodeLookupFailure,
} from '../services/postcodes'

export interface SetEngineerPostcodeResult {
  /** True when the postcode resolved and the session was written. */
  ok: boolean
  /**
   * The canonical postcode that was stored, or null when the lookup failed.
   * Callers should display this rather than the raw input so the UI shows the
   * same value the backend holds.
   */
  postcode: string | null
  /** Resolved coordinates, or null when the postcode was invalid. */
  coordinates: PostcodeCoordinates | null
  /**
   * True when the backend recorded a real move — false for a first-time set
   * or a same-postcode resubmit. Null when the save failed, leaving the
   * caller with no server answer to act on.
   */
  locationChanged: boolean | null
  /** Lines the backend kept in the basket, or null when the save failed. */
  itemCount: number | null
  /**
   * Why the postcode couldn't be set, or null on success. Callers show
   * POSTCODE_FAILURE_MESSAGES for it.
   */
  failure: PostcodeLookupFailure | null
}

/** A result for a postcode that couldn't be set, with the reason why. */
function failedResult(failure: PostcodeLookupFailure): SetEngineerPostcodeResult {
  return {
    ok: false,
    postcode: null,
    coordinates: null,
    locationChanged: null,
    itemCount: null,
    failure,
  }
}

interface MeCacheShape {
  last_postcode?: string
  [key: string]: unknown
}

/**
 * Shared "set the engineer's working postcode" flow, used by both the
 * postcode-entry screen (JobSetup) and the Change Location drawer.
 *
 * Resolves the postcode to its canonical spelling and coordinates, persists it
 * to the backend (Cart + User), keeps the `auth-me` cache in lockstep so the
 * cross-device sync guard does not fire, and writes the tab session.
 * Navigation is intentionally left to the caller — this hook only sets the
 * location.
 *
 * Everything downstream stores the canonical postcode, never the raw input:
 * saving "SW100AD" as typed leaves the user's profile holding a postcode that
 * suppliers reject at order time (OCTO-550159).
 */
export function useSetEngineerPostcode() {
  const queryClient = useQueryClient()
  const { cart, setPostcode: setPostcodeMutation } = useCart()
  const { data: meData } = useMe()
  const { tenantSlug } = useParams<{ tenantSlug: string }>()

  // Recording recents here means every route into a location change — typed,
  // picked from suggestions, found from the device, or tapped from the recent
  // chips — is remembered, without each caller having to know about it.
  const recentLocationsKey = buildRecentLocationsKey(tenantSlug, meData?.id)
  const branchPriorityStorageKey = buildBranchPriorityStorageKey(
    tenantSlug,
    meData?.id,
  )

  /**
   * @param atBranches The branches the engineer chose to show first from this
   *   location, or none. Stored on the session and each given a "Show first"
   *   tick (DEV-51045).
   */
  const setEngineerPostcode = async (
    postcode: string,
    atBranches: EngineerSessionAtBranch[] = [],
  ): Promise<SetEngineerPostcodeResult> => {
    const trimmedPostcode = postcode.trim()
    if (!trimmedPostcode) {
      return failedResult('not_found')
    }

    // Tells "doesn't exist" apart from "couldn't ask", so the engineer isn't
    // told a real postcode is wrong when the service simply didn't answer.
    const check = await checkPostcode(trimmedPostcode)
    if (check.kind === 'not_found') {
      return failedResult('not_found')
    }
    if (check.kind === 'failed') {
      return failedResult(describeFailedPostcodeCheck())
    }
    const resolved = check.resolved

    // Canonical spelling from postcodes.io, e.g. "SW100AD" → "SW10 0AD". This
    // is what gets stored and shown everywhere below.
    const { postcode: canonicalPostcode, ...coordinates } = resolved

    // Whether the backend saw a real move, and how many lines it kept. Stay
    // null when the save fails so the caller can tell "no move" from "no
    // answer" and skip announcing a basket it cannot vouch for.
    let locationChanged: boolean | null = null
    let itemCount: number | null = null

    // Persist to backend (Cart + User models). A failure here must not block
    // the user — the session write below still lets them browse this tab.
    try {
      const response = await setPostcodeMutation({ postcode: canonicalPostcode })
      locationChanged = response.location_changed
      itemCount = response.item_count

      // Keep the auth-me cache aligned with the new postcode so the
      // cross-device sync guard (usePostcodeSync) does not detect a mismatch
      // and bounce the user back to the postcode screen. Query key must match
      // useMe.ts (['auth-me']).
      queryClient.setQueryData(
        ['auth-me'],
        (oldData: MeCacheShape | undefined) => {
          if (!oldData) return oldData
          return { ...oldData, last_postcode: canonicalPostcode }
        },
      )
    } catch (error) {
      console.error('Failed to save postcode to backend:', error)
    }

    // Read before the write below overwrites it: moving away from a branch
    // has to drop its tick (AC6).
    const previousAtBranches =
      parseEngineerSession(readEngineerSessionRaw())?.atBranches ?? []

    // The canonical spelling goes everywhere the postcode is kept, recents
    // included, so a chip reads "SW10 0AD" like the pill rather than however
    // it happened to be typed.
    writeEngineerSession(canonicalPostcode, coordinates, atBranches)
    rememberRecentLocation(recentLocationsKey, canonicalPostcode)

    // Each branch the engineer has moved away from drops its tick — unless
    // the basket still holds lines there, in which case it is an ordinary
    // basket pill the engineer ticked and theirs to untick (AC6). Branches
    // chosen again this time keep theirs.
    const keptBranchIds = new Set(
      atBranches.map((branch) => String(branch.branchId)),
    )
    for (const previous of previousAtBranches) {
      if (keptBranchIds.has(String(previous.branchId))) continue
      const basketHasLinesThere = cart.some(
        (item) => String(item.branchId) === String(previous.branchId),
      )
      if (basketHasLinesThere) continue
      untickBranchPriority(
        branchPriorityStorageKey,
        branchPriorityKey(previous.supplierId, previous.branchId),
      )
    }

    // Tick each chosen branch so its results lead the search straight away.
    // Safe to persist even though they are session-only: their pills come
    // from the session, so next session the ticks have no pill and drop.
    for (const atBranch of atBranches) {
      tickBranchPriority(
        branchPriorityStorageKey,
        branchPriorityKey(atBranch.supplierId, atBranch.branchId),
      )
    }

    return {
      ok: true,
      postcode: canonicalPostcode,
      coordinates,
      locationChanged,
      itemCount,
      failure: null,
    }
  }

  return { setEngineerPostcode }
}
